/*
 *  Copyright (C) 2017 Atelier Cartographique <contact@atelier-cartographique.be>
 *
 *  This program is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, version 3 of the License.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { none } from 'fp-ts/lib/Option';
import { ReactNode } from 'react';
import { __forceRefreshState, getMapCrsName } from 'sdi/app';
import {
    BUTTON,
    DIV,
    H2,
    H3,
    LABEL,
    SPAN,
    STRONG,
} from 'sdi/components/elements';
import { helpText } from 'sdi/components/helptext';
import {
    attrOptions,
    inputNullableNumber,
    inputText,
} from 'sdi/components/input';
import { isENTER } from 'sdi/components/keycodes';
import { iife, scopeOption } from 'sdi/lib';
import tr from 'sdi/locale';
import {
    Coord2D,
    InteractionPosition,
    lonlatTo4326,
    lonlatToMapProjection,
} from 'sdi/map';
import { tryNumber } from 'sdi/util';
import {
    setPage,
    updatePositionerCoordinates,
    updatePositionerLatitude,
    updatePositionerLongitude,
} from '../../events/legend';
import {
    putMark,
    startPointerPosition,
    startTracker,
    stopPointerPosition,
    viewEvents,
} from '../../events/map';
import queries from '../../queries/legend';
import { getPointerPosition } from '../../queries/map';
import bookmark from '../bookmark';
import { makeIcon, makeLabelAndIcon } from '../button';
import { fromNullable } from 'fp-ts/lib/Option';

const setPositionGPS = (input: string) =>
    parseCoordinates(input).map(coords => {
        updatePositionerCoordinates(...coords);
        position();
    });

const getPositionerPos = () => queries.toolsPositioner().point;

const getPositionerGPS = () => {
    const { longitude, latitude } = getPositionerPos();
    if (longitude === 0 || latitude === 0) {
        // a better solution would be to set type of 'component/legend/positioner'
        // to Nullable<IPositioner>
        return '';
    }
    const coords = lonlatTo4326(longitude, latitude);
    return formatCoordsGPS(coords);
};

// google maps 50.84655310353309, 4.342918658893667
// OSM         50.848176/4.343848
// well, lets call it a consensus to format it as `latitude\SEP\longitude`

const formatCoordsGPS = (coords: Coord2D) =>
    coords
        .reverse()
        .map(n => n.toFixed(6))
        .join(', ');

// AKA WGS84
const gpsInput = () =>
    LABEL(
        'gps-input',
        tr.view('GPscoordinates'),
        inputText(
            attrOptions('gps-input', () => getPositionerGPS(), setPositionGPS, {
                placeholder: tr.view('latitude-longitude'),
            }),
        ),
    );

const isValidWGS84 = (lat: number, lon: number) =>
    lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;

const parseCoordinates = (input: string) => {
    const parts = input.trim().split(/[,/:\s]+/);
    return scopeOption()
        .let('lat', tryNumber(parts[0]))
        .let('lon', tryNumber(parts[1]))
        .filter(({ lat, lon }) => isValidWGS84(lat, lon))
        .map(({ lat, lon }) => lonlatToMapProjection(lon, lat));
};

const position = () => {
    const state = queries.toolsPositioner();
    viewEvents.updateMapView({
        center: [state.point.longitude, state.point.latitude],
        dirty: 'geo',
    });
    putMark([state.point.longitude, state.point.latitude]);
};

const wrapLocation = (...children: ReactNode[]) =>
    DIV(
        { className: 'sidebar-main tool location' },
        H2({}, tr.view('location')),
        DIV(
            { className: 'tool-body lat-lon' },
            DIV({}, tr.view('helptext:locationTool')),
            ...children,
        ),
    );
const wrapBookmark = (...children: ReactNode[]) =>
    DIV(
        { className: 'sidebar-main tool location' },
        H2({}, tr.view('addBookmark')),
        DIV(
            { className: 'tool-body lat-lon' },
            DIV({}, tr.view('helptext:bookmark')),
            ...children,
        ),
    );

const btnStopPointerPosition = makeLabelAndIcon('close', 2, 'stop', () =>
    tr.view('stop'),
);

const renderLatLon = ({ state }: InteractionPosition) =>
    DIV(
        'cursor-location',
        H2({}, tr.view('cursorLocalisation')),
        helpText(
            tr.view('helptext:cursorLocationOn', { crs: getMapCrsName() }),
        ),

        DIV(
            'cursor-location__part',
            H3('', tr.view('GPStype')),
            SPAN(
                '',
                STRONG('', tr.view('GPscoordinates'), ' : '),
                formatCoordsGPS(
                    lonlatTo4326(state.coordinates[0], state.coordinates[1]),
                ),
            ),
        ),

        DIV(
            'cursor-location__part',
            H3('', getMapCrsName()),
            DIV(
                'cursor-location__content',
                SPAN(
                    '',
                    STRONG('', tr.view('longitude'), ' : '),
                    state.coordinates[0].toFixed(),
                ), // FIXME: coordinates could be something else than meters
                SPAN(
                    '',
                    STRONG('', tr.view('latitude'), ' : '),
                    state.coordinates[1].toFixed(),
                ),
            ),
        ),

        btnStopPointerPosition(() => stopPointerPosition(none)),
    );

const renderPointerPosition = (interactionPosition: InteractionPosition) =>
    wrapLocation(renderLatLon(interactionPosition));

const renderBookmark = (interactionPosition: InteractionPosition) =>
    wrapBookmark(renderLatLon(interactionPosition));

const getOrNull = (n: number) => (n === 0 ? null : n);

const latitudeInput = () =>
    LABEL(
        'label-latitude',
        tr.view('latitude'),
        inputNullableNumber(
            attrOptions(
                'legend-tool-positioner-lat',
                () => getOrNull(getPositionerPos().latitude),
                updatePositionerLatitude,
                {
                    name: 'lat',
                },
            ),
        ),
    );

const longitudeInput = () =>
    LABEL(
        'label-longitude',
        tr.view('longitude'),
        inputNullableNumber(
            attrOptions(
                'legend-tool-positioner-lon',
                () => getOrNull(getPositionerPos().longitude),
                updatePositionerLongitude,
                {
                    name: 'lon',
                    onKeyPress: e => {
                        if (isENTER(e)) {
                            position();
                        }
                    },
                },
            ),
        ),
    );

const btnPointerLocationStart = makeLabelAndIcon(
    'start',
    2,
    'crosshairs-gps',
    () => tr.view('cursorLocalisation'),
);
const btnLocationSearch = makeIcon('start', 2, 'search', {
    position: 'top-left',
    text: () => tr.core('search'),
});

type LocationInputType = 'map' | 'gps';

const { getInputType, setInputType } = iife(() => {
    let inputType: LocationInputType = 'gps';
    const getInputType = () => inputType;
    const setInputType = (t: LocationInputType) => (inputType = t);
    return { getInputType, setInputType };
});

const crsNameMap: Record<string, string> = {
    'EPSG:31370': 'Lambert 72',
    'EPSG:4326': 'GPS (WGS84)',
};

const tabTitle = (inputType: LocationInputType) => {
    const mapSSR = getMapCrsName();
    switch (inputType) {
        case 'map':
            return fromNullable(crsNameMap[mapSSR]).getOrElse(mapSSR);
        case 'gps':
            return 'GPS (WGS84)';
    }
};

const renderTab = (inputType: LocationInputType, current: LocationInputType) =>
    inputType === current
        ? BUTTON(`tab selected tab-${inputType}`, tabTitle(inputType))
        : BUTTON(
              {
                  className: `tab  tab-${inputType} active`,
                  onClick: () => {
                      setInputType(inputType);
                      __forceRefreshState(); // yeah, i know
                  },
              },
              tabTitle(inputType),
          );

const renderLocationInputHeader = (current: LocationInputType) =>
    DIV('tabs__list', renderTab('gps', current), renderTab('map', current));

const pointLocationInner = (inputType: LocationInputType) => {
    switch (inputType) {
        case 'map':
            return DIV(
                'point-location__inner',
                DIV(
                    'lat-lon-inputs',
                    longitudeInput(),
                    latitudeInput(),
                    btnLocationSearch(position),
                ),
                helpText(
                    tr.view('helptext:pointLocationTool', {
                        crs: getMapCrsName(),
                    }),
                ),
            );
        case 'gps':
            return DIV(
                'point-location__inner',
                gpsInput(),
                DIV('helptext', tr.view('helptext:GPScoordinatesTool')),
            );
    }
};

const pointLocation = () =>
    DIV(
        'point-location',
        H3({}, tr.view('pointLocation')),
        renderLocationInputHeader(getInputType()),
        pointLocationInner(getInputType()),
    );

const pointerLocation = () =>
    DIV(
        'pointer-location',
        H3({}, tr.view('cursorLocalisation')),
        DIV(
            {},
            btnPointerLocationStart(() => {
                setPage('locate');
                startPointerPosition(putMark);
            }),
        ),
        helpText(tr.view('helptext:cursorLocationOff')),
    );

const btnStartTracker = makeLabelAndIcon('start', 2, 'location-arrow', () =>
    tr.view('startGPS'),
);

const gpsTracker = () =>
    DIV(
        {},
        H3({}, tr.core('gpsTracker')),
        btnStartTracker(startTracker),
        helpText(tr.view('helptext:gpsTracker')),
    );

const renderInput = () =>
    wrapLocation(pointLocation(), pointerLocation(), gpsTracker(), bookmark());

const render = (currentPage: 'locate' | 'locate-bookmark' = 'locate') => {
    switch (currentPage) {
        case 'locate':
            return getPointerPosition().foldL(
                renderInput,
                renderPointerPosition,
            );
        case 'locate-bookmark':
            return getPointerPosition().foldL(renderInput, renderBookmark);
    }
};

export default render;
